import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faSpinner, faPlay, faStop } from '@fortawesome/free-solid-svg-icons';
import ModalOverlay from '../../../components/ui/ModalOverlay/ModalOverlay';
import s from '../CaseFilePage.module.css';

interface PreIssueChecklistForm {
  form_input_fees: string;
  form_input_consent: string;
  form_input_cl_details: string;
  form_input_def_details: string;
  form_input_scf: string;
  form_input_poc: string;
  form_input_cov_let: string;
  form_input_cert: string;
  form_input_res_pack: string;
  form_input_comments: string;
}

interface PreIssueChecklistModalProps {
  preIssueForm: PreIssueChecklistForm;
  setPreIssueForm: React.Dispatch<React.SetStateAction<PreIssueChecklistForm>>;
  preIssueTimerRunning: boolean;
  preIssueTimerElapsed: number;
  preIssueTimerStart: number | null;
  setPreIssueTimerStart: (v: number | null) => void;
  setPreIssueTimerRunning: (v: boolean) => void;
  preIssueTimerRef: React.MutableRefObject<ReturnType<typeof setInterval> | null>;
  handleSavePreIssueChecklist: () => void;
  isPending: boolean;
  formatElapsed: (ms: number) => string;
  onClose: () => void;
}

export default function PreIssueChecklistModal({
  preIssueForm,
  setPreIssueForm,
  preIssueTimerRunning,
  preIssueTimerElapsed,
  preIssueTimerStart: _preIssueTimerStart,
  setPreIssueTimerStart,
  setPreIssueTimerRunning,
  preIssueTimerRef,
  handleSavePreIssueChecklist,
  isPending,
  formatElapsed,
  onClose,
}: PreIssueChecklistModalProps) {
  const handleClose = () => {
    if (preIssueTimerRunning && !window.confirm('Timer is running. Close without saving?')) return;
    onClose();
    setPreIssueTimerRunning(false);
    if (preIssueTimerRef.current) clearInterval(preIssueTimerRef.current);
  };

  return (
    <ModalOverlay onClose={handleClose} title="Pre-Issue Checklist">
      <div className={s.modalForm}>
        {/* Timer */}
        <div className={s.riskTimerRow}>
          <span className={s.riskTimerDisplay}>{formatElapsed(preIssueTimerElapsed)}</span>
          {!preIssueTimerRunning ? (
            <button
              className={s.btnPrimary}
              onClick={() => {
                setPreIssueTimerStart(Date.now() - preIssueTimerElapsed);
                setPreIssueTimerRunning(true);
              }}
            >
              <FontAwesomeIcon icon={faPlay} /> Start
            </button>
          ) : (
            <button
              className={s.btnDanger}
              onClick={() => {
                setPreIssueTimerRunning(false);
                if (preIssueTimerRef.current) clearInterval(preIssueTimerRef.current);
              }}
            >
              <FontAwesomeIcon icon={faStop} /> Stop
            </button>
          )}
        </div>

        {/* Checklist Fields */}
        {(
          [
            { key: 'form_input_fees', label: 'Help with Fees' },
            { key: 'form_input_consent', label: 'Client Consent Obtained' },
            { key: 'form_input_cl_details', label: 'Client Details Checked' },
            { key: 'form_input_def_details', label: 'Defendant Details Checked' },
            { key: 'form_input_scf', label: 'Sealed Claim Form' },
            { key: 'form_input_poc', label: 'Signed POCs' },
            { key: 'form_input_cov_let', label: 'Signed Cover Letter' },
            { key: 'form_input_cert', label: 'Certificate of Service' },
            { key: 'form_input_res_pack', label: 'Response Pack' },
          ] as const
        ).map(({ key, label }) => (
          <div key={key} className={s.formGroup}>
            <label>{label}</label>
            <select
              value={preIssueForm[key]}
              onChange={(e) => setPreIssueForm((p) => ({ ...p, [key]: e.target.value }))}
            >
              <option value="">Select…</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
              <option value="N/A">N/A</option>
            </select>
          </div>
        ))}

        <div className={s.formGroup}>
          <label>Assessor Comments</label>
          <textarea
            rows={4}
            value={preIssueForm.form_input_comments}
            onChange={(e) =>
              setPreIssueForm((p) => ({ ...p, form_input_comments: e.target.value }))
            }
            placeholder="Additional notes or comments..."
          />
        </div>

        <div className={s.modalActions}>
          <button className={s.btnSecondary} onClick={handleClose}>
            Cancel
          </button>
          <button
            className={s.btnPrimary}
            onClick={handleSavePreIssueChecklist}
            disabled={isPending}
          >
            {isPending ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faCheck} />
            )}
            Save Checklist
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
